import styles from "./map.module.css";
import mapboxgl from "mapbox-gl";
import { useCallback, useRef, useEffect, MouseEvent, useMemo } from "react";
import { drawMapElements, MapDrawingConfig } from "../utils/canvas-drawing";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { MapUIOverlay } from "./overlay/MapUIOverlay";
import { MapElement } from "../utils/canvas-drawing";
import { useElementDragging } from "./use-element-dragging";
import { isCoordinateInsideBounds } from "PosterEditor/utils/coordinate-utils";
import {
  mapboxLngLatBoundsFromCoordinateBounds,
  defaultLngLatBounds,
} from "PosterEditor/hooks/use-coordinates";
import { useSizePresets } from "PosterEditor/model/size-presets/size-preset";

import { useEditorUI } from "PosterEditor/use-editor-ui";
import { CoordinateBounds } from "PosterEditor/hooks/use-coordinates";

interface MapProps {
  onMapLoad: (mapReference: any) => void;
  onStaticImageURLFetched: (url: string) => void;
  onClick: () => void;
  onMove: () => void;
  onChangeElementUnderCursor: (element: MapElement | undefined) => void;
  onShowElementEditor: (show: boolean) => void;
  onLoadCanvas: (canvas: any) => void;
  isMobile?: boolean;
}

export const Map = (props: MapProps) => {
  const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ?? "";
  mapboxgl.accessToken = accessToken;
  const {
    project,
    setCoordinateBounds: saveBoundsToProject,
    gpsSource,
    isTemplateEditing,
    imageCache,
    styledPointsRequiringBoundsUpdate,
    clearStyledPointsRequiringBoundsUpdate,
  } = usePosterProject();
  const {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    pointerLocation,
    setPointerLocation,
    isDragging,
    setDraggingElement,
    elementUnderCursor,
    setElementUnderCursor,
    setSnapLines,
    setMapRef,
  } = useElementDragging();
  const { editorUIState, setMapSizeInPX } = useEditorUI();
  const mapContainer = useRef<any>(null);
  const map = useRef<any>(null);
  const canvasOverlay = useRef<any>(null);
  const {
    isLoading: sizePresetsLoading,
    cssAspectRatio,
    incrementSizeInPX,
  } = useSizePresets();

  const listenToMapMoveEvents = () => {
    map.current.on("move", () => {
      props.onMove();
    });

    // Only save bounds when movement is complete to avoid triggering re-renders on every frame
    map.current.on("moveend", () => {
      const bounds = map.current.getBounds();
      const northeast = bounds.getNorthEast();
      const southwest = bounds.getSouthWest();

      const coordinateBounds: CoordinateBounds = {
        northEast: {
          lat: northeast.lat,
          lng: northeast.lng,
        },
        southWest: {
          lat: southwest.lat,
          lng: southwest.lng,
        },
      };
      saveBoundsToProject(coordinateBounds);
    });
  };

  useEffect(() => {
    if (!map.current) {
      return;
    }
    if (editorUIState.editingElement) {
      map.current.keyboard.disable();
    } else {
      map.current.keyboard.enable();
    }
  }, [editorUIState.editingElement]);

  useEffect(() => {
    props.onLoadCanvas(canvasOverlay.current);
  }, [canvasOverlay]);

  useEffect(() => {
    if (map.current) {
      const zoom = map.current.getZoom();
      if (zoom) {
        map.current.easeTo({
          zoom: zoom + 0.4,
        });
      }
    }
  }, [editorUIState.mapZoomInCounter]);

  useEffect(() => {
    if (map.current) {
      const zoom = map.current.getZoom();
      if (zoom) {
        map.current.easeTo({
          zoom: zoom - 0.4,
        });
      }
    }
  }, [editorUIState.mapZoomOutCounter]);

  // CONFIGURE THE MAP.
  useEffect(() => {
    if (!map.current) {
      createMap();
    }
  }, [project.mapStyle, props]);

  useEffect(() => {
    map.current.resize();
    drawContent();
  }, [project.borderStyle, project.sizeOption]);

  const createMap = () => {
    const bounds = project.bounds
      ? mapboxLngLatBoundsFromCoordinateBounds(project.bounds)
      : defaultLngLatBounds;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: project.mapStyle,
      bounds: bounds,
    });
    map.current.dragRotate.disable(); // Disables right-click drag rotation
    map.current.touchZoomRotate.disableRotation(); // Disables two-finger rotation on touch devices
    map.current.keyboard.disableRotation(); // Disables keyboard-based rotation (e.g. arrow keys + ctrl)
    map.current.doubleClickZoom.disable(); // (Optional) disables double-click zoom if undesired
    map.current.pitchEnabled = false;
    listenToMapMoveEvents();
    props.onMapLoad(map);

    setMapRef(map);

    if (map.current && project.bounds) {
      const bounds = project.bounds
        ? mapboxLngLatBoundsFromCoordinateBounds(project.bounds)
        : defaultLngLatBounds;
      map.current.fitBounds(bounds, {
        padding: 0, // Optional: padding around the bounds
        duration: 0.3, // Optional: disable animation if needed
      });

      //map.current.dragPan.disable();
    }
  };

  useEffect(() => {
    const bounds = project.bounds
      ? mapboxLngLatBoundsFromCoordinateBounds(project.bounds)
      : defaultLngLatBounds;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: project.mapStyle,
      bounds: bounds,
    });
    map.current.dragRotate.disable(); // Disables right-click drag rotation
    map.current.touchZoomRotate.disableRotation(); // Disables two-finger rotation on touch devices
    map.current.keyboard.disableRotation(); // Disables keyboard-based rotation (e.g. arrow keys + ctrl)
    map.current.doubleClickZoom.disable(); // (Optional) disables double-click zoom if undesired
    map.current.pitchEnabled = false;
    listenToMapMoveEvents();
    props.onMapLoad(map);
  }, [project.mapStyle]);

  // HANDLE MOUSE POINTER INTERUPT (disabled on mobile)
  useEffect(() => {
    if (props.isMobile) return; // Skip pointer tracking on mobile

    const handleMouseMove = (event: MouseEvent) => {
      if (!mapContainer.current) return;

      // Calculate the mouse position relative to the mapContainer
      const rect = mapContainer.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      setPointerLocation({ x, y });
    };

    const handleMouseLeave = () => {
      setPointerLocation(undefined);
    };

    const container = mapContainer.current;
    if (container) {
      container.addEventListener("mousemove", handleMouseMove);
      container.addEventListener("mouseleave", handleMouseLeave);
    }

    // Cleanup the event listener on component unmount
    return () => {
      if (container) {
        container.removeEventListener("mousemove", handleMouseMove);
        container.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, [props.isMobile]);

  // CENTER THE MAP ON LOADING COORDINATES
  useEffect(() => {
    let coords = gpsSource?.allCoordinates ?? [];
    let newStylesPointsRequireBoundsUpdate =
      styledPointsRequiringBoundsUpdate.length > 0;

    let styledPointCoords = project.styledPoints.map(
      (point) => point.coordinate
    );

    if (project.bounds) {
      const anyPointOutsideBounds = project.styledPoints.some((point) => {
        return !isCoordinateInsideBounds(point.coordinate, project.bounds!);
      });
      if (!anyPointOutsideBounds) {
        newStylesPointsRequireBoundsUpdate = false;
        clearStyledPointsRequiringBoundsUpdate();
      }
    }

    coords = coords.concat(styledPointCoords);

    if (!newStylesPointsRequireBoundsUpdate && project.bounds) {
      const bounds = mapboxLngLatBoundsFromCoordinateBounds(project.bounds);
      map.current.fitBounds(bounds, {
        padding: 0,
        duration: 1000,
      });
      requestAnimationFrame(drawContent);
    } else if (coords && coords.length > 1) {
      // Create a bounding box from the coordinates
      const bounds = new mapboxgl.LngLatBounds();
      // Extend the bounds to include each coordinate

      coords.forEach((coord) => {
        bounds.extend([coord.lng, coord.lat]);
      });

      //Extend the bounds by 10% to give some padding.
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();

      const latDiff = ne.lat - sw.lat;
      const lngDiff = ne.lng - sw.lng;

      // Expand by 10%
      const latExpansion = latDiff * 0.1;
      const lngExpansion = lngDiff * 0.1;

      const newSw = new mapboxgl.LngLat(
        sw.lng - lngExpansion,
        sw.lat - latExpansion
      );
      const newNe = new mapboxgl.LngLat(
        ne.lng + lngExpansion,
        ne.lat + latExpansion
      );

      bounds.extend(newSw);
      bounds.extend(newNe);

      map.current.fitBounds(bounds, { duration: 3000 });
      clearStyledPointsRequiringBoundsUpdate();
    } else if (coords && coords.length === 1) {
      // If there's only one coordinate, just center the map on it
      map.current.setCenter([coords[0].lng, coords[0].lat]);
      map.current.setZoom(15); // Or whatever zoom level you prefer for a single point
    }
  }, [gpsSource?.allCoordinates, project.styledPoints.length]);

  const increment = useMemo(
    () => incrementSizeInPX(project),
    [project.sizeOption]
  );

  const drawContent = () => {
    if (mapContainer.current === undefined || mapContainer.current === null) {
      return;
    }

    const drawingConfig: MapDrawingConfig = {
      map,
      canvasElement: canvasOverlay,
      originalMapSize: {
        width: mapContainer.current.offsetWidth,
        height: mapContainer.current.offsetHeight,
      },
      scale: 1,
      project,
      gpsSource,
      dotGridSpacing: isDragging ? increment : undefined,
      pointerLocation,
      pointerInteruptRadius: 50,
      elementToHighlight: undefined,
      selectedElement: editorUIState.editingElement,
      isRendering: false,
      projectEditingPermissions: project.editingPermissions,
      isTemplateEditing: isTemplateEditing,
      imageCache: imageCache,
    };

    const drawResult = drawMapElements(drawingConfig);

    const elementUnderCursor = drawResult.elementUnderPointerLocation;
    setSnapLines({
      vertical: drawResult.snapLines.vertical,
      horizontal: drawResult.snapLines.horizontal,
    });

    const width: number = canvasOverlay.current.width;
    const height: number = canvasOverlay.current.height;
    setMapSizeInPX({ width, height });

    const bounds = map.current.getBounds();
    if (!bounds) {
      return;
    }
    //map.current.dragPan.disable();

    if (map) {
      const type = elementUnderCursor?.type;
      if (
        type === "mapLabel" ||
        type === "staticLabel" ||
        type === "backdrop"
      ) {
        map.current.dragPan.disable();
      } else {
        map.current.dragPan.enable();
      }
    }
    setElementUnderCursor(elementUnderCursor);
    props.onChangeElementUnderCursor(elementUnderCursor);
  };

  // DRAW CONTENT ON MAP.
  useEffect(() => {
    if (map) {
      requestAnimationFrame(drawContent);
      const drawWithAnimationFrame = () => {
        // setEditingElement(undefined);
        requestAnimationFrame(drawContent);
      };

      map.current.on("move", drawWithAnimationFrame);
      map.current.on("zoom", drawWithAnimationFrame);
      // map.current.on("idle", drawWithAnimationFrame);

      return () => {
        map.current.off("move", drawWithAnimationFrame);
        map.current.off("zoom", drawWithAnimationFrame);
        // map.current.on("idle", drawWithAnimationFrame);
      };
    }
  }, [
    map,
    gpsSource?.displayedCoordinates,
    pointerLocation,
    project.lineStyle,
    project.styleUpdateKey,
    project.mapLabels,
    project.labelUpdateKey,
    editorUIState.editingElement,
    project.backdrops,
    project.backdropUpdateKey,
    project.sizeOption,
    imageCache,

    // editingElement,
  ]);

  useEffect(() => {
    if (!map.current || !mapContainer.current) return;

    map.current.resize();
    setMapRef(map);

    const handleIdle = () => {
      requestAnimationFrame(() => {
        if (canvasOverlay.current && mapContainer.current) {
          canvasOverlay.current.width = mapContainer.current.offsetWidth;
          canvasOverlay.current.height = mapContainer.current.offsetHeight;
        }

        drawContent();
      });

      map.current.off("idle", handleIdle);
    };

    map.current.on("idle", handleIdle);
  }, [project.sizeOption]);
  // const drawStaticMapCanvasOverlay = () => {
  //   if (map) {
  //     drawSmoothedLineWithParallelLines({
  //       map,
  //       coordinates: project.gpsSources[0].displayedCoordinates,
  //       targetRef: staticCanvasOverlay,
  //       originalMapWidth: mapContainer.current.offsetWidth,
  //       originalMapHeight: mapContainer.current.offsetHeight,
  //     });
  //   }
  // };
  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!elementUnderCursor) {
      setDraggingElement(undefined);
    } else {
      setDraggingElement(elementUnderCursor);
    }
  };

  if (sizePresetsLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div
      className={styles.mapContainer}
      onClick={props.onClick}
      style={{
        width: "100%",
        height: "100%",
      }}
    >
      <div
        ref={mapContainer}
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "3px",
          pointerEvents: "auto",
        }}
        onClick={props.isMobile ? undefined : onClick}
        onMouseDown={props.isMobile ? undefined : handleMouseDown}
        onMouseMove={props.isMobile ? undefined : handleMouseMove}
        onMouseUp={props.isMobile ? undefined : handleMouseUp}
        onMouseLeave={props.isMobile ? undefined : handleMouseUp}
      />
      <canvas
        ref={canvasOverlay}
        className={styles.canvasOverlay}
        width={mapContainer.current?.offsetWidth}
        height={mapContainer.current?.offsetHeight}
      />
      <canvas
        // ref={canvasOverlay}
        className={styles.canvasOverlay2}
        width={mapContainer.current?.offsetWidth}
        height={mapContainer.current?.offsetHeight}
      />
      <div
        className={styles.uiOverlay}
        style={{ aspectRatio: cssAspectRatio(project) }}
      >
        <MapUIOverlay
          pointerLocation={pointerLocation}
          isDragging={isDragging}
          map={map}
          elementUnderCursor={elementUnderCursor}
          onShowElementEditor={(element) => {
            // setEditingElement(element);
          }}
          isMobile={props.isMobile}
        />
      </div>
    </div>
  );
};
